import React, { useState } from 'react';
import { Card, Badge, Button, Spinner, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import moment from 'moment';
import 'moment/locale/es';
import { aprobarSolicitud, rechazarSolicitud } from '../../api/common';

moment.locale('es');

const TarjetaSolicitud = ({ solicitud, usuarioActual, onVerDetalle, onActualizar }) => {
  const [procesando, setProcesando] = useState(false);

  // Verificar si el usuario actual puede aprobar esta solicitud
  const puedeAprobar = () => {
    if (!solicitud.responsablesAprobacion) return false;

    const responsable = solicitud.responsablesAprobacion.find(
      r => r.usuario._id === usuarioActual._id && r.estado === 'pendiente'
    );

    return !!responsable && solicitud.estado !== 'rechazado' && solicitud.estado !== 'cancelado';
  };

  const esCreador = solicitud.solicitante._id === usuarioActual._id;

  // Obtener badge de estado
  const getBadgeEstado = (estado) => {
    const badges = {
      'pendiente': { variant: 'warning', icon: 'clock-history', text: 'Pendiente' },
      'en_revision': { variant: 'info', icon: 'eye', text: 'En Revisión' },
      'aprobado': { variant: 'success', icon: 'check-circle-fill', text: 'Aprobado' },
      'rechazado': { variant: 'danger', icon: 'x-circle-fill', text: 'Rechazado' },
      'cancelado': { variant: 'secondary', icon: 'dash-circle', text: 'Cancelado' }
    };
    const badge = badges[estado] || badges['pendiente'];
    return (
      <Badge bg={badge.variant} className="px-3 py-2">
        <i className={`bi bi-${badge.icon} me-1`}></i>
        {badge.text}
      </Badge>
    );
  };

  // Obtener icono y color por tipo de solicitud
  const getTipoInfo = (tipo) => {
    const tipos = {
      'permiso': { icon: 'calendar-check', color: 'warning', label: 'Permiso' },
      'vacaciones': { icon: 'sun', color: 'info', label: 'Vacaciones' },
      'insumo': { icon: 'box', color: 'purple', label: 'Insumo' },
      'dotacion': { icon: 'shop', color: 'teal', label: 'Dotación' },
      'capacitacion': { icon: 'mortarboard', color: 'danger', label: 'Capacitación' },
      'incapacidad': { icon: 'hospital', color: 'orange', label: 'Incapacidad' },
      'compensatorio': { icon: 'clock', color: 'cyan', label: 'Compensatorio' },
      'teletrabajo': { icon: 'house-door', color: 'success', label: 'Teletrabajo' },
      'reembolso': { icon: 'cash-coin', color: 'dark', label: 'Reembolso' },
      'equipamiento': { icon: 'laptop', color: 'secondary', label: 'Equipamiento' },
      'mantenimiento': { icon: 'tools', color: 'brown', label: 'Mantenimiento' },
      'horasExtras': { icon: 'alarm', color: 'warning', label: 'Horas Extras' },
      'otro': { icon: 'three-dots', color: 'muted', label: 'Otro' }
    };
    return tipos[tipo] || tipos['otro'];
  };

  const handleAprobar = async () => {
    const result = await Swal.fire({
      title: '¿Aprobar solicitud?',
      html: `
        <p>Estás a punto de aprobar la solicitud:</p>
        <strong>${solicitud.titulo}</strong>
        <textarea 
          id="comentarios-aprobacion" 
          class="form-control mt-3" 
          placeholder="Comentarios (opcional)" 
          rows="3"
        ></textarea>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: '<i class="bi bi-check-lg me-2"></i>Aprobar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        return document.getElementById('comentarios-aprobacion').value;
      }
    });

    if (result.isConfirmed) {
      setProcesando(true);
      try {
        const comentarios = result.value || '';
        const resultado = await aprobarSolicitud(solicitud._id,{ usuarioId: usuarioActual._id, comentarios: result.value || '' });

        if (resultado.status==200) {
          Swal.fire({
            title: '¡Aprobado!',
            text: 'La solicitud ha sido aprobada exitosamente',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          onActualizar();
        } else {
          throw new Error(resultado.error || 'Error al aprobar');
        }
      } catch (error) {
        console.error('Error:', error);
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo aprobar la solicitud',
          icon: 'error'
        });
      } finally {
        setProcesando(false);
      }
    }
  };

  const handleRechazar = async () => {
    const result = await Swal.fire({
      title: '¿Rechazar solicitud?',
      html: `
        <p>Estás a punto de rechazar la solicitud:</p>
        <strong>${solicitud.titulo}</strong>
        <textarea 
          id="comentarios-rechazo" 
          class="form-control mt-3" 
          placeholder="Motivo del rechazo (requerido)" 
          rows="3"
          required
        ></textarea>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: '<i class="bi bi-x-lg me-2"></i>Rechazar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const comentarios = document.getElementById('comentarios-rechazo').value;
        if (!comentarios.trim()) {
          Swal.showValidationMessage('Debes indicar el motivo del rechazo');
        }
        return comentarios;
      }
    });

    if (result.isConfirmed) {
      setProcesando(true);
      try {
        const comentarios = result.value;
        const resultado = await rechazarSolicitud(solicitud._id,{ usuarioId: usuarioActual._id, comentarios: result.value || '' });

        if (resultado.status==200) {
          Swal.fire({
            title: 'Rechazado',
            text: 'La solicitud ha sido rechazada',
            icon: 'info',
            timer: 2000,
            showConfirmButton: false
          });
          onActualizar();
        } else {
          throw new Error(resultado.error || 'Error al rechazar');
        }
      } catch (error) {
        console.error('Error:', error);
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo rechazar la solicitud',
          icon: 'error'
        });
      } finally {
        setProcesando(false);
      }
    }
  };

  const tipoInfo = getTipoInfo(solicitud.tipoSolicitud);

  return (
    <Card className="border-0 shadow-sm hover-shadow transition w-100">
      <Card.Body className="w-100">
        <Row className="row g-3 align-items-stretch">
          {/* Columna 2: Información Principal */}
          <Col md={9} >
            <Card className="bg-light border-0 mb-0 h-100 w-100">
              <Card.Body className="py-3">
                <div className="row g-3">

                  {/* Título de la Solicitud */}
                  <div className="col-10">
                    <div className="d-flex align-items-center">
                      <div className={`rounded-3 p-3 text-center mb-2`}>
                        <i className={`bi bi-${tipoInfo.icon} text-${tipoInfo.color} me-2 fs-3`}></i>
                      </div>
                      <div>
                        <small className="text-muted d-block">Solicitud</small>
                        <div className="d-flex align-items-center gap-2">
                          <strong>{solicitud.titulo}</strong>
                          {/* Badge de notas — incita al usuario a entrar al detalle a leer la conversación. */}
                          {(solicitud.notas?.length || 0) > 0 && (
                            <Badge
                              bg="warning"
                              text="dark"
                              className="d-inline-flex align-items-center gap-1 px-2 py-1"
                              title={`${solicitud.notas.length} nota(s) · entra al detalle para leerlas`}
                              style={{ fontSize: '0.78rem' }}
                            >
                              <i className="bi bi-chat-left-text-fill" />
                              {solicitud.notas.length > 99 ? '99+' : solicitud.notas.length}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-2">
                  {esCreador && (
                      <Badge bg="primary" className="px-3 py-2 text-center">
                        <i className="bi bi-person-badge me-1"></i>
                        Mi solicitud
                      </Badge>
                    )}
                  </div>

                  {/* Solicitante */}
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-person-circle text-primary me-2 fs-5"></i>
                      <div>
                        <small className="text-muted d-block">Solicitante</small>
                        <strong className="small">{solicitud.solicitante.nombre} {solicitud.solicitante.apellido}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Fecha Inicio */}
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-calendar3 text-success me-2 fs-5"></i>
                      <div>
                        <small className="text-muted d-block">Fecha Inicio</small>
                        <strong className="small">
                          {moment.utc(solicitud.fechaInicio).format("DD/MM/YYYY")}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* Fecha Fin */}
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-calendar-check text-danger me-2 fs-5"></i>
                      <div>
                        <small className="text-muted d-block">Fecha Fin</small>
                        <strong className="small">
                          {moment.utc(solicitud.fechaFin).format("DD/MM/YYYY")}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* Duración */}
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-clock-history text-warning me-2 fs-5"></i>
                      <div>
                        <small className="text-muted d-block">Duración</small>
                        <strong className="small">{solicitud.duracionDias} día(s)</strong>
                      </div>
                    </div>
                  </div>

                  {/* Estado */}
                  <div className="col-12 col-sm-6 col-lg-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-flag text-info me-2 fs-5"></i>
                      <div>
                        <small className="text-muted d-block">Estado</small>
                        {getBadgeEstado(solicitud.estado)}
                      </div>
                    </div>
                  </div>

                  {/* Descripción */}
                  {solicitud.descripcion && (
                    <div className="col-12 col-sm-6 col-lg-9 border-top">
                      <div className="d-flex align-items-start">
                        <i className="bi bi-chat-left-text text-info me-2 fs-5 mt-1"></i>
                        <div>
                          <small className="text-muted d-block mb-1">Descripción:</small>
                          <Card.Text className="mb-0 small text-muted">
                            {solicitud.descripcion.length > 150
                              ? `${solicitud.descripcion.substring(0, 150)}...`
                              : solicitud.descripcion
                            }
                          </Card.Text>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botones de Acción */}
                  <div className="col-12 d-flex align-items-center flex-wrap gap-2 mt-3 pt-2 border-top">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => onVerDetalle(solicitud)}
                      title="Ver detalle completo"
                    >
                      <i className="bi bi-eye me-2"></i>
                      Ver Detalle
                    </Button>

                    {puedeAprobar() && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={handleAprobar}
                          disabled={procesando}
                          title="Aprobar solicitud"
                        >
                          {procesando ? (
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                          ) : (
                            <i className="bi bi-check-lg me-2"></i>
                          )}
                          Aprobar
                        </Button>

                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleRechazar}
                          disabled={procesando}
                          title="Rechazar solicitud"
                        >
                          {procesando ? (
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                          ) : (
                            <i className="bi bi-x-lg me-2"></i>
                          )}
                          Rechazar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Columna 3: Flujo de Aprobación */}
          <Col md={3} >
            {solicitud.responsablesAprobacion && solicitud.responsablesAprobacion.length > 0 && (
              <Card className="border-1 border-secondary h-100">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    <i className="bi bi-diagram-3 text-info me-2 fs-5"></i>
                    <strong className="text-muted">Flujo de Aprobación</strong>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {solicitud.responsablesAprobacion.map((resp, index) => {
                      const isActual = resp.usuario._id === usuarioActual._id;
                      let badgeVariant = 'secondary';
                      let icon = 'circle';

                      if (resp.estado === 'aprobado') {
                        badgeVariant = 'success';
                        icon = 'check-circle-fill';
                      } else if (resp.estado === 'rechazado') {
                        badgeVariant = 'danger';
                        icon = 'x-circle-fill';
                      } else if (resp.estado === 'pendiente') {
                        badgeVariant = 'warning';
                        icon = 'hourglass-split';
                      }

                      return (
                        <Badge
                          key={index}
                          bg={badgeVariant}
                          className={`px-3 py-2 d-flex align-items-center ${isActual ? 'border border-3 border-dark' : ''}`}
                          title={`${resp.usuario.nombre} - ${resp.estado}`}
                        >
                          <i className={`bi bi-${icon} me-2`}></i>
                          <span>
                            {resp.orden}° {resp.usuario.nombre.split(' ')[0]}
                            {isActual && ' (Tú)'}
                          </span>
                        </Badge>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Card.Body>

      {/* Footer Card con Metadata */}
      <Card.Footer className="bg-light border-top w-100">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <small className="text-muted">
            <i className="bi bi-clock me-1"></i>
            Creado: {moment.utc(solicitud.createdAt).format('DD/MM/YYYY HH:mm')}
          </small>
          <small className="text-muted">
            <i className="bi bi-arrow-repeat me-1"></i>
            Actualizado: {moment.utc(solicitud.updatedAt).fromNow()}
          </small>
        </div>
      </Card.Footer>
    </Card>
);
};

export default TarjetaSolicitud;








